import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spinner, StatusBadge } from '../common';
import type { StatusBadgeTone } from '../common';
import { adminApi } from '../../api/admin';
import { ConversionDiagnostics as Diagnostics } from '../../types';
import { formatReportDate } from './ReportDetail';

type StageState = 'ok' | 'failed' | 'pending';

const STAGE_TONES: Record<StageState, StatusBadgeTone> = {
  ok: 'ok',
  failed: 'error',
  pending: 'off',
};

export function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Work out which stage the pipeline reached from the artifacts it left behind.
 * Each stage persists its output before the next begins, so on a failure the
 * first stage with no output is where it broke.
 */
export function deriveStages(d: Diagnostics): Record<'received' | 'converted' | 'audio', StageState> {
  const failed = d.status === 'failed';
  const received = d.sourceType === 'text' ? !!(d.textContent || d.transcript) : !!d.rawHtmlPath;
  const converted = !!d.transcript;
  const audio = !!d.audioPath;
  const state = (done: boolean, reached: boolean): StageState =>
    done ? 'ok' : failed && reached ? 'failed' : 'pending';
  return {
    received: state(received, true),
    converted: state(converted, received),
    audio: state(audio, converted),
  };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 font-body text-sm text-foreground break-words">{children}</div>
    </div>
  );
}

function Payload({ label, content, note }: { label: string; content: string; note?: string }) {
  return (
    <details className="border border-border rounded-sm">
      <summary className="cursor-pointer px-3 py-2 font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground">
        {label} · {content.length.toLocaleString()} chars{note ? ` · ${note}` : ''}
      </summary>
      <pre className="max-h-80 overflow-auto border-t border-border bg-background px-3 py-2 font-mono text-xs text-foreground whitespace-pre-wrap break-all">
        {content}
      </pre>
    </details>
  );
}

function Stage({
  index,
  title,
  state,
  upstreamFailed,
  children,
}: {
  index: number;
  title: string;
  state: StageState;
  upstreamFailed: boolean;
  children: React.ReactNode;
}) {
  const label =
    state === 'ok' ? 'Done' : state === 'failed' ? 'Failed here' : upstreamFailed ? 'Not reached' : 'Pending';
  return (
    <li
      aria-label={`${title}: ${label}`}
      className={`border rounded-sm p-4 space-y-3 ${state === 'failed' ? 'border-destructive' : 'border-border'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-display text-base font-medium text-foreground">
          <span className="font-mono text-xs text-muted-foreground mr-2">{index}</span>
          {title}
        </h4>
        <StatusBadge tone={STAGE_TONES[state]}>{label}</StatusBadge>
      </div>
      {children}
    </li>
  );
}

const Missing = ({ children }: { children: React.ReactNode }) => (
  <p className="font-body text-sm text-muted-foreground italic">{children}</p>
);

export default function ConversionDiagnostics({ reportId }: { reportId: string }) {
  const [data, setData] = useState<Diagnostics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminApi.getDiagnostics(reportId));
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    void load();
  }, [load]);

  const header = (
    <div className="flex items-center justify-between gap-2">
      <h3 className="font-display text-xl font-medium text-foreground">Pipeline diagnostics</h3>
      {data && (
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      )}
    </div>
  );

  if (loading && !data) {
    return (
      <section aria-label="Pipeline diagnostics" className="bg-card border border-border rounded-sm p-6 space-y-4">
        {header}
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section aria-label="Pipeline diagnostics" className="bg-card border border-border rounded-sm p-6 space-y-4">
        {header}
        <p className="font-body text-sm text-destructive">{error ?? 'No diagnostics available'}</p>
        <Button variant="secondary" size="sm" onClick={() => void load()}>
          Try again
        </Button>
      </section>
    );
  }

  const stages = deriveStages(data);
  const failed = data.status === 'failed';
  const isText = data.sourceType === 'text';
  const isEmail = data.sourceType === 'email';

  return (
    <section aria-label="Pipeline diagnostics" className="bg-card border border-border rounded-sm p-6 space-y-4">
      {header}

      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={failed ? 'error' : data.status === 'completed' ? 'ok' : 'live'}>
          {data.status ?? 'unknown'}
        </StatusBadge>
        {data.renewalCount > 0 && (
          <span className="font-mono text-xs tracking-wide uppercase text-muted-foreground">
            renewed {data.renewalCount}× · last {data.renewedAt ? formatReportDate(data.renewedAt) : '—'}
          </span>
        )}
      </div>

      {data.errorMessage && (
        <div role="status" className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="font-mono text-xs tracking-[0.14em] uppercase text-destructive">
            Error{data.errorCode ? ` · ${data.errorCode}` : ''}
          </p>
          <p className="mt-1 font-body text-sm text-destructive break-words">{data.errorMessage}</p>
        </div>
      )}

      <ol className="space-y-3">
        <Stage index={1} title="Request" state="ok" upstreamFailed={false}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Source type">{data.sourceType}</Field>
            <Field label="Requested">{data.createdAt ? formatReportDate(data.createdAt) : '—'}</Field>
            <div className="sm:col-span-2">
              <Field label="Source">
                <code className="font-mono text-xs break-all">{data.url || '—'}</code>
              </Field>
            </div>
            {isEmail && (
              <>
                <Field label="From">{data.emailFrom || '—'}</Field>
                <Field label="Subject">{data.emailSubject || '—'}</Field>
              </>
            )}
            <Field label="Title">{data.title || '—'}</Field>
            <Field label="Author">{data.author || '—'}</Field>
          </div>
        </Stage>

        <Stage
          index={2}
          title={isText ? 'Content submitted' : 'Content received'}
          state={stages.received}
          upstreamFailed={false}
        >
          {isText ? (
            data.textContent ? (
              <Payload label="Submitted text" content={data.textContent} />
            ) : (
              <Missing>
                {data.transcript
                  ? 'Renewal — replayed the stored transcript rather than the original text.'
                  : 'No text content was stored.'}
              </Missing>
            )
          ) : data.rawHtmlPath ? (
            <>
              <Field label="Stored at">
                <code className="font-mono text-xs break-all">{data.rawHtmlPath}</code>
              </Field>
              {data.rawHtml != null ? (
                <Payload
                  label={isEmail ? 'Email HTML' : 'Fetched HTML'}
                  content={data.rawHtml}
                  note={
                    data.rawHtmlTruncated
                      ? `truncated, ${formatBytes(data.rawHtmlSize)} total`
                      : formatBytes(data.rawHtmlSize)
                  }
                />
              ) : (
                <Missing>Could not load captured HTML: {data.rawHtmlError ?? 'unknown error'}</Missing>
              )}
            </>
          ) : (
            <Missing>
              {isEmail ? 'The email body was never captured.' : 'The page was never fetched.'}
            </Missing>
          )}
        </Stage>

        <Stage
          index={3}
          title="Conversion"
          state={stages.converted}
          upstreamFailed={failed && stages.received !== 'ok'}
        >
          {data.transcript ? (
            <>
              <Field label="Words extracted">{data.wordCount?.toLocaleString() ?? '—'}</Field>
              <Payload label="SSML sent to text-to-speech" content={data.transcript} />
            </>
          ) : (
            <Missing>No transcript was produced.</Missing>
          )}
        </Stage>

        <Stage
          index={4}
          title="Audio"
          state={stages.audio}
          upstreamFailed={failed && stages.converted !== 'ok'}
        >
          {data.audioPath ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Duration">{formatDuration(data.duration)}</Field>
              <Field label="Size">{formatBytes(data.fileSize)}</Field>
              <Field label="Completed">{data.completedAt ? formatReportDate(data.completedAt) : '—'}</Field>
              <div className="sm:col-span-3">
                <Field label="Stored at">
                  <code className="font-mono text-xs break-all">{data.audioPath}</code>
                </Field>
              </div>
            </div>
          ) : (
            <Missing>No audio was generated.</Missing>
          )}
        </Stage>
      </ol>

      {data.updatedAt && (
        <p className="font-mono text-xs tracking-wide uppercase text-muted-foreground">
          last pipeline update {formatReportDate(data.updatedAt)}
        </p>
      )}
    </section>
  );
}
